import type { MyScenario } from './scenario';

/** 仅保留场景标识及本机使用时间，不缓存权限、提示词或其他账号数据。 */
export interface ScenarioRecentUsage {
  scenarioId: number;
  lastUsedAt: number;
}
/** 首页卡片仅组合平台场景和本机使用时间，展示层不再自行排序。 */
export interface HomeScenarioCard {
  scenario: MyScenario;
  lastUsedAt: number | null;
}

/** 读取不可信本地记录；按最近时间去重，最多保留二十项，畸形字段不会进入界面。 */
export function trimScenarioRecentUsage(value: unknown): ScenarioRecentUsage[] {
  if (!Array.isArray(value)) return [];
  const seen = new Set<number>();
  return value
    .slice(0, 1000)
    .filter(
      (item): item is ScenarioRecentUsage =>
        item &&
        typeof item === 'object' &&
        Number.isSafeInteger(item.scenarioId) &&
        item.scenarioId > 0 &&
        Number.isFinite(item.lastUsedAt) &&
        item.lastUsedAt > 0 &&
        item.lastUsedAt <= Date.now() + 60_000
    )
    .sort((a, b) => b.lastUsedAt - a.lastUsedAt)
    .filter((item) => {
      if (seen.has(item.scenarioId)) return false;
      seen.add(item.scenarioId);
      return true;
    })
    .slice(0, 20)
    .map(({ scenarioId, lastUsedAt }) => ({ scenarioId, lastUsedAt }));
}

/** 沿用 BoMate 的最近优先和分组轮换；只推荐当前平台已授权且启用的场景。 */
export function buildHomeScenarioCardPool(
  scenarios: MyScenario[],
  recent: ScenarioRecentUsage[]
): HomeScenarioCard[] {
  const enabled = new Map(
    scenarios.filter((scenario) => scenario.status !== 0).map((scenario) => [scenario.id, scenario])
  );
  const cards: HomeScenarioCard[] = [];
  for (const usage of trimScenarioRecentUsage(recent)) {
    const scenario = enabled.get(usage.scenarioId);
    if (!scenario) continue;
    cards.push({ scenario, lastUsedAt: usage.lastUsedAt });
    enabled.delete(scenario.id);
  }
  const ranked = [...enabled.values()].sort(
    (a, b) =>
      Number(b.is_recommended) - Number(a.is_recommended) ||
      Number(b.is_hot) - Number(a.is_hot) ||
      a.sort_order - b.sort_order ||
      b.usage_count - a.usage_count ||
      a.id - b.id
  );
  const groups = new Map<number, MyScenario[]>();
  for (const scenario of ranked) {
    const group = groups.get(scenario.group_id) ?? [];
    group.push(scenario);
    groups.set(scenario.group_id, group);
  }
  // 使用平台分组 ID 做均衡，避免把中文分类名称写死后破坏其他语言或租户分类。
  let activeGroups = [...groups.values()];
  for (let index = 0; activeGroups.length > 0; index++) {
    const nextGroups: MyScenario[][] = [];
    for (const group of activeGroups) {
      cards.push({ scenario: group[index], lastUsedAt: null });
      if (index + 1 < group.length) nextGroups.push(group);
    }
    // 已耗尽的分组不再遍历，使分组数很多时的轮换成本仍与场景数量成正比。
    activeGroups = nextGroups;
  }
  return cards;
}

/** 返回当前语言的相对时间；历史日期保留本地日期格式，未来的小幅时钟偏差按刚刚处理。 */
export function formatScenarioLastUsedAt(
  timestamp: number,
  language: string,
  now = Date.now()
): string {
  const locale = language === 'zh-TW' ? 'zh-TW' : language === 'en' ? 'en' : 'zh-CN';
  const seconds = Math.max(0, (now - timestamp) / 1000);
  if (seconds >= 7 * 86400) return new Intl.DateTimeFormat(locale).format(timestamp);
  const unit = seconds >= 86400 ? 'day' : seconds >= 3600 ? 'hour' : 'minute';
  const count = Math.floor(seconds / (unit === 'day' ? 86400 : unit === 'hour' ? 3600 : 60));
  return new Intl.RelativeTimeFormat(locale, { numeric: 'auto' }).format(-count, unit);
}
